import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getSubscriptionStatus } from '../services/revenueCatService';
import type { SubscriptionStatusResponse, SubscriptionPlatformInfo } from '../types/subscription';

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

interface SubscriptionStore {
    // State
    status: SubscriptionStatusResponse | null;
    lastFetchTime: number | null;
    isLoading: boolean;
    error: Error | null;
    isInitialized: boolean;

    // Actions
    fetchStatus: (force?: boolean) => Promise<void>;
    clearStatus: () => void;
    setError: (error: Error | null) => void;
    initialize: () => Promise<void>;

    // Selectors
    isActive: () => boolean;
    getActiveSubscription: () => SubscriptionPlatformInfo | null;
    shouldRefetch: () => boolean;
}

export const useSubscriptionStore = create<SubscriptionStore>()(
    devtools(
        (set, get) => ({
            // Initial state
            status: null,
            lastFetchTime: null,
            isLoading: false,
            error: null,
            isInitialized: false,

            // Actions
            initialize: async () => {
                const store = get();
                if (store.isInitialized) return;

                await store.fetchStatus();
                set({ isInitialized: true });
            },

            fetchStatus: async (force = false) => {
                const store = get();
                
                // Skip if already loading
                if (store.isLoading) return;

                // Skip if we have recent data and not forcing refresh
                if (!force && !store.shouldRefetch()) return;

                set({ isLoading: true, error: null });

                try {
                    const status = await getSubscriptionStatus();
                    set({
                        status,
                        lastFetchTime: Date.now(),
                        isLoading: false,
                        error: null
                    });
                } catch (error) {
                    set({
                        error: error instanceof Error ? error : new Error('Failed to fetch subscription status'),
                        isLoading: false
                    });
                    // Keep the last known good state if we have it
                    if (!get().status) {
                        set({ status: null });
                    }
                }
            },

            clearStatus: () => set({
                status: null,
                lastFetchTime: null,
                error: null,
                isInitialized: false
            }),

            setError: (error) => set({ error }),

            // Selectors
            isActive: () => get().status?.isActive ?? false,
            
            getActiveSubscription: () => get().status?.activeSubscription ?? null,
            
            shouldRefetch: () => {
                const { lastFetchTime, status } = get();
                if (!lastFetchTime || !status) return true;
                return Date.now() - lastFetchTime > CACHE_TTL;
            }
        }),
        {
            name: 'subscription-store',
            // Only persist the subscription status and last fetch time
            partialize: (state: SubscriptionStore) => ({
                status: state.status,
                lastFetchTime: state.lastFetchTime
            })
        }
    )
); 